import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { topUniversities } from "@/data/universities";
import { supabase } from "@/integrations/supabase/client";

interface InstitutionSelectProps {
  value: string; // institution ID
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type: "university" | "school";
  otherValue?: string; // institution_other_name value
  onOtherChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const PAGE_SIZE = 15;

const InstitutionSelect = ({
  value,
  onChange,
  type = "university",
  otherValue = "",
  onOtherChange,
}: InstitutionSelectProps) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [options, setOptions] = useState<{ id: string; name_en: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [defaultInstitution, setDefaultInstitution] = useState<{
    id: string;
    name_en: string;
  } | null>(null);
  const [open, setOpen] = useState(false);
  const latestValueRef = useRef<string>("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch default selected institution by ID
  useEffect(() => {
    latestValueRef.current = value;
    if (value) {
      supabase
        .from("institutions")
        .select("id, name_en")
        .eq("id", value)
        .single()
        .then(({ data }) => {
          if (latestValueRef.current === value) {
            if (data) {
              setDefaultInstitution(data);
              setOptions([data]);
            } else {
              setDefaultInstitution(null);
            }
          }
        });
    } else {
      setDefaultInstitution(null);
    }
  }, [value, type]);

  // Fetch institutions on search/type change
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const query = supabase
      .from("institutions")
      .select("id, name_en")
      .ilike("name_en", `%${debouncedSearch}%`)
      .eq("type", type === "school" ? "high_school" : "university")
      .order("ranking", { ascending: true })
      .limit(PAGE_SIZE);
    query.then(({ data }) => {
      setOptions(data || []);
      setLoading(false);
    });
  }, [debouncedSearch, type, open]);

  // Reset selection and options when type changes
  useEffect(() => {
    setOptions([]);
    setDefaultInstitution(null);
    setSearch("");
    // if (value) {
    //   // Reset the selection in the parent
    //   const event = {
    //     target: {
    //       name: "institution",
    //       value: "",
    //     },
    //   } as React.ChangeEvent<HTMLInputElement>;
    //   onChange(event);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Handle select change
  const handleInstitutionChange = (selectedId: string) => {
    const event = {
      target: {
        name: "institution",
        value: selectedId,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(event);

    // Clear institution_other_name when selecting a regular institution
    if (selectedId !== "other" && onOtherChange) {
      const clearOtherEvent = {
        target: {
          name: "institution_other_name",
          value: "",
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onOtherChange(clearOtherEvent);
    }
  };

  // Handle other institution name change
  const handleOtherNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onOtherChange) {
      onOtherChange(e);
    }
  };

  // Show the default institution as placeholder if value is set
  const placeholder =
    value === "other"
      ? "Other"
      : value && defaultInstitution?.name_en
      ? defaultInstitution.name_en
      : `Select your ${type}`;

  return (
    <div className="space-y-6">
      <div>
        <label
          htmlFor="institution"
          className="block text-sm font-medium text-gray-700 mb-1 capitalize"
        >
          {type} *
        </label>
        <Select
          value={value}
          onValueChange={handleInstitutionChange}
          open={open}
          onOpenChange={(newOpen) => {
            setOpen(newOpen);
            if (newOpen) {
              setTimeout(() => {
                searchInputRef.current?.focus();
              }, 50); // Increased delay to allow Radix to finish its focus management
            }
          }}
        >
          <SelectTrigger className="w-full px-4 py-2 border-2 border-neutral-light rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all bg-white text-gray-900">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="bg-white border shadow-lg max-h-[300px]">
            <div className="px-2 py-2">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={`Search for a ${type}...`}
                className="w-full px-2 py-1 border rounded"
                ref={searchInputRef}
              />
            </div>
            {/* Other option */}
            <SelectItem
              value="other"
              className="focus:bg-[#233A74] focus:text-white hover:bg-[#233A74] hover:text-white data-[highlighted]:bg-[#233A74] data-[highlighted]:text-white font-medium border-b border-gray-200"
            >
              Other (not listed)
            </SelectItem>

            {defaultInstitution && (
              <SelectItem
                key={defaultInstitution.id}
                value={defaultInstitution.id}
                className="focus:bg-[#233A74] focus:text-white hover:bg-[#233A74] hover:text-white data-[highlighted]:bg-[#233A74] data-[highlighted]:text-white"
              >
                {defaultInstitution.name_en}
              </SelectItem>
            )}
            {loading ? (
              <SelectItem value="loading-indicator" disabled>
                Loading...
              </SelectItem>
            ) : options.length === 0 ? (
              <SelectItem value="no-results-found" disabled>
                No results found
              </SelectItem>
            ) : (
              options
                .filter((opt) => opt.id !== defaultInstitution?.id)
                .map((inst) => (
                  <SelectItem
                    key={inst.id}
                    value={inst.id}
                    className="focus:bg-[#233A74] focus:text-white hover:bg-[#233A74] hover:text-white data-[highlighted]:bg-[#233A74] data-[highlighted]:text-white"
                  >
                    {inst.name_en}
                  </SelectItem>
                ))
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Show institution name input when "Other" is selected */}
      {value === "other" && (
        <div>
          <label
            htmlFor="institution_other_name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Institution Name *
          </label>
          <Input
            id="institution_other_name"
            name="institution_other_name"
            type="text"
            value={otherValue}
            onChange={handleOtherNameChange}
            placeholder={`Enter your ${type} name`}
            className="w-full px-4 py-2 border-2 border-neutral-light rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all bg-white text-gray-900"
          />
        </div>
      )}
    </div>
  );
};

export default InstitutionSelect;
